import React, { useEffect, useState } from 'react'
import { Link, NavLink, useNavigation } from '@remix-run/react'
import { TbArrowUpRight, TbChevronRight } from 'react-icons/tb'
import { IconType } from 'react-icons'
import { useBoolean } from 'usehooks-ts'
import clsx from 'clsx'

import { Hamburger } from './hamburger'
import { CallToAction } from '~/utils/a-b'
import { Disclosure } from '../disclosure/disclosure'
import { NavCard } from '../../components-v2/navCard/navCard'
import { Header } from '~/components-v2/header/header'
import { Stack } from '~/components-v2'
import { menuItems, NavItem } from '~/config/navData'

const NavWrapper = ({
  to,
  className,
  children,
}: {
  to?: string
  className: string
  children: React.ReactNode
}) =>
  to ? (
    <NavLink to={to} className={className}>
      {children}
    </NavLink>
  ) : (
    <span className={className}> {children}</span>
  )

const renderIcon = (Icon?: IconType) =>
  Icon ? React.createElement(Icon, { color: 'var(--color-eggplant)' }) : null

export const ContentHeader = ({
  mainRef,
}: {
  mainRef: React.RefObject<HTMLDivElement | null>
}) => {
  const { value: hasMenu, toggle, setFalse: closeMenu } = useBoolean()
  const navigation = useNavigation()

  const [activeMenu, setActiveMenu] = useState<'main' | 'sub'>('main')
  const [currentItems, setCurrentItems] = useState<NavItem[][]>([])
  const [currentTitle, setCurrentTitle] = useState('')

  const handleMobileNavClick = (columns: NavItem[][], title: string) => {
    setCurrentItems(columns)
    setCurrentTitle(title)
    setActiveMenu('sub')
  }

  useEffect(() => {
    const main = mainRef.current

    if (!main) return
    if (hasMenu) {
      main.classList.add('blur')
    } else {
      main.classList.remove('blur')
    }
  }, [hasMenu, mainRef])

  useEffect(() => {
    if (hasMenu && navigation.state !== 'idle') {
      closeMenu()
    }
  }, [closeMenu, hasMenu, navigation.state])

  useEffect(() => {
    setActiveMenu('main')
  }, [hasMenu])

  return (
    <Header
      className={clsx(hasMenu && ' is-active')}
      onClickWordmark={closeMenu}
      showBackArrow={hasMenu && activeMenu === 'sub'}
      onClickBackArrow={() => setActiveMenu('main')}
    >
      <Stack row gap={2} className="header-actions-mobile">
        <Link className="call-to-action size-md demo" to="/request-demo">
          Request Demo
        </Link>
        <CallToAction intent="primary" className="size-md" />

        <button
          className="hamburger-menu"
          aria-pressed={hasMenu}
          aria-label="Toggle menu"
          onClick={toggle}
        >
          <Hamburger />
        </button>
      </Stack>

      {/* Desktop Nav */}
      <nav className="nav desktop">
        <Stack row justify="space-between" grow={1} className="header-menu">
          {menuItems.map((item) => {
            if (item.columns) {
              return (
                <Disclosure
                  key={item.title}
                  title={item.title}
                  megaMenu={item.megaMenu}
                >
                  <Stack row gap={4}>
                    {item.columns.map((column, colIndex) => (
                      <Stack
                        gap={0}
                        className="nav-sub-column"
                        key={colIndex.toString()}
                      >
                        {column.map((child) => {
                          return (
                            <NavWrapper
                              key={child.label}
                              to={child.to}
                              className="stack nav-card"
                            >
                              <NavCard
                                to={child.to}
                                icon={renderIcon(child.icon)}
                                title={child.label || ''}
                                subTitle={child.subTitle}
                                externalLink={child.externalLink}
                              >
                                {child.children && (
                                  <Stack gap={2} className="nav-sub-column">
                                    {child.children.map((nested) =>
                                      nested.externalLink ? (
                                        <NavCard
                                          key={nested.label}
                                          icon={renderIcon(nested.icon)}
                                          title={nested.label || ''}
                                          subTitle={nested.subTitle}
                                          externalLink={true}
                                          to={nested.to}
                                        />
                                      ) : (
                                        nested.to && (
                                          <NavLink
                                            key={nested.to}
                                            to={nested.to}
                                          >
                                            <NavCard
                                              icon={renderIcon(nested.icon)}
                                              title={nested.label || ''}
                                              subTitle={nested.subTitle}
                                              to={nested.to}
                                              externalLink={nested.externalLink}
                                            />
                                          </NavLink>
                                        )
                                      ),
                                    )}
                                  </Stack>
                                )}
                              </NavCard>
                            </NavWrapper>
                          )
                        })}
                      </Stack>
                    ))}
                  </Stack>
                </Disclosure>
              )
            }

            if (!item.icon && !item.subTitle) {
              return (
                <NavLink className="nav-item" key={item.to} to={item.to!}>
                  {item.title}
                </NavLink>
              )
            }

            return (
              <NavLink className="stack nav-card" key={item.to} to={item.to!}>
                <NavCard
                  icon={renderIcon(item.icon)}
                  title={item.title}
                  subTitle={item.subTitle}
                  to={item.to}
                  externalLink={item.externalLink}
                />
              </NavLink>
            )
          })}
        </Stack>
        <Stack row gap={2} className="header-actions">
          <Link className="call-to-action size-md" to="/request-demo">
            Request Demo
          </Link>
          <CallToAction intent="primary" className="size-md" />
        </Stack>
      </nav>
      {/* Mobile Nav */}
      <nav className="nav mobile">
        <Stack
          className={clsx(
            'header-menu mobile-panel',
            activeMenu === 'main' && 'active',
          )}
        >
          {menuItems.map((item) =>
            item.columns ? (
              <a
                className="stack nav-card"
                key={item.title}
                onClick={(e) => {
                  e.preventDefault()
                  handleMobileNavClick(item.columns ?? [], item.title)
                }}
              >
                <span>{item.title}</span>
                <TbChevronRight className="nav-drawer-arrow" />
              </a>
            ) : item.to ? (
              <NavLink key={item.to} to={item.to}>
                {item.title}
              </NavLink>
            ) : null,
          )}
        </Stack>

        <Stack
          className={`header-menu mobile-panel-secondary ${activeMenu === 'sub' ? 'active' : ''}`}
        >
          <Stack className="mobile-panel-secondary-container">
            <h3>{currentTitle}</h3>
            <Stack className="mobile-nav-container" row wrap={'wrap'} gap={4}>
              {currentItems.map((column) => {
                const itemsWithChildren = column.filter(
                  (item: NavItem) => item.children,
                )
                const itemsWithoutChildren = column.filter(
                  (item: NavItem) => !item.children,
                )

                return (
                  <React.Fragment key={column[0]?.label}>
                    {itemsWithoutChildren.length > 0 && (
                      <Stack className="mobile-nav-subcolumn" gap={2}>
                        {itemsWithoutChildren.map((item: NavItem) =>
                          item.externalLink ? (
                            <a
                              key={item.label}
                              href={item.to}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => {
                                closeMenu()
                                setActiveMenu('main')
                              }}
                            >
                              <span>
                                {item.label}
                                <TbArrowUpRight />
                              </span>
                            </a>
                          ) : (
                            item.to && (
                              <NavLink
                                key={item.label}
                                to={item.to}
                                onClick={() => {
                                  closeMenu()
                                  setActiveMenu('main')
                                }}
                              >
                                {item.label}
                              </NavLink>
                            )
                          ),
                        )}
                      </Stack>
                    )}

                    {itemsWithChildren.map((item: NavItem) => (
                      <Stack
                        key={item.label}
                        className="mobile-nav-subcolumn"
                        gap={2}
                      >
                        <Disclosure title={item.label || ''}>
                          <Stack as="ul" className="mobile-subitems" gap={0}>
                            {item.children?.map((child) => (
                              <li key={child.to}>
                                {child.externalLink ? (
                                  <a
                                    href={child.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                      closeMenu()
                                      setActiveMenu('main')
                                    }}
                                  >
                                    <span>
                                      {child.label}
                                      <TbArrowUpRight />
                                    </span>
                                  </a>
                                ) : (
                                  child.to && (
                                    <NavLink
                                      to={child.to}
                                      onClick={() => {
                                        closeMenu()
                                        setActiveMenu('main')
                                      }}
                                    >
                                      {child.label}
                                    </NavLink>
                                  )
                                )}
                              </li>
                            ))}
                          </Stack>
                        </Disclosure>
                      </Stack>
                    ))}
                  </React.Fragment>
                )
              })}
            </Stack>
          </Stack>
        </Stack>
      </nav>
    </Header>
  )
}
